export interface MicroreelGenerationInfo {
  title: string;
  description: string;
  // Newest group id when the generation started (null = no groups yet).
  previousGroupId?: string | null;
  // PromptToLessonJob id returned by the generate endpoints; lets
  // generation_status be polled per generation. Absent on generations
  // started before the backend change or whose response was lost.
  generationId?: string;
  lengthLabel: string;
  visualsLabel?: string;
  visualsHint?: string;
  languageLabel: string;
  // Overrides the static "Narration and on-screen text" hint (video sources
  // keep the original audio, so the language is on-screen text only).
  languageHint?: string;
  // Language key ('en', 'es', ...) as chosen in the form: drives the
  // language of the acknowledgment screen in the learner preview.
  languageKey?: string;
  // End action chosen in the form ('none' | 'assessment' | 'ack'): with
  // anything but the knowledge check, generated in-reel questions are noise
  // and stay hidden in the group view.
  endActionKey?: string;
  voiceName: string;
  // Overrides the static "AI narration" hint (e.g. video sources keep the
  // original audio).
  voiceHint?: string;
  narrationLabel: string;
  credits: number;
  sourceFileName?: string;
  sourceFileSizeMb?: number;
  sourcePages?: number;
  sourceThumbnail?: string;
  excludedPages?: string;
  projectId?: string;
  projectName?: string;
}

export interface StoredGenerationInfo extends MicroreelGenerationInfo {
  id: string;
  startedAt: number;
}

const STORAGE_KEY = 'microreelGenerationInfo';

const storageKey = (organizationId: string) =>
  `${STORAGE_KEY}:${organizationId}`;

const newGenerationId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const writeGenerationInfos = (
  organizationId: string,
  infos: StoredGenerationInfo[],
) => {
  try {
    if (infos.length) {
      localStorage.setItem(storageKey(organizationId), JSON.stringify(infos));
    } else {
      localStorage.removeItem(storageKey(organizationId));
    }
  } catch {
    // ignore
  }
};

// Oldest first; legacy storage held a single object.
export const readGenerationInfos = (
  organizationId?: string,
): StoredGenerationInfo[] => {
  if (!organizationId) return [];
  try {
    const raw = localStorage.getItem(storageKey(organizationId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as
      | StoredGenerationInfo[]
      | (Omit<StoredGenerationInfo, 'id'> & {id?: string});
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list
      .filter(Boolean)
      .map(item => ({...item, id: item.id ?? `legacy-${item.startedAt}`}))
      .sort((a, b) => a.startedAt - b.startedAt);
  } catch {
    return [];
  }
};

export const storeGenerationInfo = (
  organizationId: string,
  info: MicroreelGenerationInfo,
): StoredGenerationInfo => {
  const stored: StoredGenerationInfo = {
    ...info,
    id: newGenerationId(),
    startedAt: Date.now(),
  };
  writeGenerationInfos(organizationId, [
    ...readGenerationInfos(organizationId),
    stored,
  ]);
  return stored;
};

export const readLatestGenerationInfo = (
  organizationId?: string,
): StoredGenerationInfo | undefined => {
  const infos = readGenerationInfos(organizationId);
  return infos[infos.length - 1];
};

export const readGenerationInfo = (
  organizationId?: string,
  generationId?: string,
): StoredGenerationInfo | undefined => {
  if (!generationId) return readLatestGenerationInfo(organizationId);
  return readGenerationInfos(organizationId).find(
    info => info.id === generationId,
  );
};

export const removeGenerationInfo = (
  organizationId?: string,
  generationId?: string,
) => {
  if (!organizationId || !generationId) return;
  writeGenerationInfos(
    organizationId,
    readGenerationInfos(organizationId).filter(
      info => info.id !== generationId,
    ),
  );
};

const RESULTS_KEY = 'microreelGenerationResults';
const RESULT_TTL_MS = 60 * 60 * 1000;

export interface StoredGenerationResult {
  id: string;
  groupId?: string;
  failed?: boolean;
  at: number;
}

const resultsKey = (organizationId: string) =>
  `${RESULTS_KEY}:${organizationId}`;

const readGenerationResults = (
  organizationId?: string,
): StoredGenerationResult[] => {
  if (!organizationId) return [];
  try {
    const raw = localStorage.getItem(resultsKey(organizationId));
    return raw ? (JSON.parse(raw) as StoredGenerationResult[]) : [];
  } catch {
    return [];
  }
};

// The first tab to settle a generation drops it from the tracked list, so the
// outcome is kept for the tab that started it.
export const storeGenerationResult = (
  organizationId: string,
  result: Omit<StoredGenerationResult, 'at'>,
) => {
  const at = Date.now();
  const kept = readGenerationResults(organizationId).filter(
    item => item.id !== result.id && at - item.at < RESULT_TTL_MS,
  );
  try {
    localStorage.setItem(
      resultsKey(organizationId),
      JSON.stringify([...kept, {...result, at}]),
    );
  } catch {
    // ignore
  }
};

export const readGenerationResult = (
  organizationId?: string,
  generationId?: string,
): StoredGenerationResult | undefined => {
  if (!generationId) return undefined;
  return readGenerationResults(organizationId).find(
    item => item.id === generationId,
  );
};

export const clearGenerationInfo = (organizationId?: string) => {
  if (!organizationId) return;
  writeGenerationInfos(organizationId, []);
};

const GROUP_SETTINGS_KEY = 'microreelGroupSettings';

// Snapshot of the generation settings, kept per created group so the group
// details view can show what was actually chosen (the backend does not
// return generation settings on the group).
export const storeGroupSettings = (
  groupId: string,
  info: MicroreelGenerationInfo,
) => {
  try {
    localStorage.setItem(
      `${GROUP_SETTINGS_KEY}:${groupId}`,
      JSON.stringify(info),
    );
  } catch {
    // ignore
  }
};

export const readGroupSettings = (
  groupId: string,
): MicroreelGenerationInfo | undefined => {
  try {
    const raw = localStorage.getItem(`${GROUP_SETTINGS_KEY}:${groupId}`);
    return raw ? (JSON.parse(raw) as MicroreelGenerationInfo) : undefined;
  } catch {
    return undefined;
  }
};
